import { useState } from 'react';
import { Card, CardContent, Input } from '../../../components/ui';
import ImageUploader from './ImageUploader';

interface BrandingSectionProps {
    tagline?: string;
    onTaglineChange: (value: string) => void;
    logo?: File | string;
    onLogoChange: (file: File | null) => void;
    // Hero Banner Content
    heroBannerTitle?: string;
    onHeroBannerTitleChange: (value: string) => void;
    heroBannerSubtitle?: string;
    onHeroBannerSubtitleChange: (value: string) => void;
    heroBannerHighlightLabel?: string;
    onHeroBannerHighlightLabelChange: (value: string) => void;
    heroBannerCtaText?: string;
    onHeroBannerCtaTextChange: (value: string) => void;
    heroBannerRedirectionUrl?: string;
    onHeroBannerRedirectionUrlChange: (value: string) => void;
    // SEO
    metaTitle?: string;
    onMetaTitleChange: (value: string) => void;
    metaDescription?: string;
    onMetaDescriptionChange: (value: string) => void;
    metaKeywords?: string[];
    onMetaKeywordsChange: (keywords: string[]) => void;
    ogImage?: File | string;
    onOgImageChange: (file: File | null) => void;
}

export default function BrandingSection({
    tagline,
    onTaglineChange,
    logo,
    onLogoChange,
    heroBannerTitle,
    onHeroBannerTitleChange,
    heroBannerSubtitle,
    onHeroBannerSubtitleChange,
    heroBannerHighlightLabel,
    onHeroBannerHighlightLabelChange,
    heroBannerCtaText,
    onHeroBannerCtaTextChange,
    heroBannerRedirectionUrl,
    onHeroBannerRedirectionUrlChange,
    metaTitle,
    onMetaTitleChange,
    metaDescription,
    onMetaDescriptionChange,
    metaKeywords = [],
    onMetaKeywordsChange,
    ogImage,
    onOgImageChange,
}: BrandingSectionProps) {
    const [keywordInput, setKeywordInput] = useState('');

    const handleAddKeyword = (e: React.KeyboardEvent<HTMLInputElement>) => {
        if (e.key === 'Enter' && keywordInput.trim()) {
            e.preventDefault();
            if (!metaKeywords.includes(keywordInput.trim())) {
                onMetaKeywordsChange([...metaKeywords, keywordInput.trim()]);
            }
            setKeywordInput('');
        }
    };

    const handleRemoveKeyword = (index: number) => {
        onMetaKeywordsChange(metaKeywords.filter((_, i) => i !== index));
    };

    return (
        <Card>
            <CardContent className="p-6 space-y-6">
                <div className="flex items-center gap-3 pb-4 border-b border-[#e2e8f0]">
                    <div className="w-10 h-10 rounded-lg bg-gradient-to-br from-[#10b981] to-[#34d399] flex items-center justify-center flex-shrink-0">
                        <span className="text-white font-bold text-lg">2</span>
                    </div>
                    <div>
                        <h3 className="text-lg font-semibold text-[#1e293b]">Branding & Content</h3>
                        <p className="text-sm text-[#64748b]">
                            Add branding elements and SEO metadata for your microsite
                        </p>
                    </div>
                </div>

                {/* Tagline & Logo - Two Column */}
                <div className="grid grid-cols-1 lg:grid-cols-2 gap-6">
                    <div className="space-y-2">
                        <label className="block text-sm font-medium text-[#1e293b]">Tagline</label>
                        <Input
                            type="text"
                            value={tagline || ''}
                            onChange={(e) => onTaglineChange(e.target.value)}
                            placeholder="Your perfect tagline for the microsite"
                        />
                        <p className="text-xs text-[#64748b]">
                            A catchy tagline to describe your brand or store
                        </p>
                    </div>

                    {/* Logo */}
                    <ImageUploader
                        label="Logo"
                        value={logo}
                        onChange={onLogoChange}
                        aspectRatio="1:1"
                        maxSize={2}
                    />
                </div>

                {/* Hero Banner Content Section */}
                <div className="pt-4 border-t border-[#e2e8f0]">
                    <div className="flex items-center gap-2 mb-4 bg-[#fef3c7] p-3 rounded-lg border border-[#fde68a]">
                        <svg
                            className="w-5 h-5 text-[#f59e0b]"
                            fill="none"
                            stroke="currentColor"
                            viewBox="0 0 24 24"
                        >
                            <path
                                strokeLinecap="round"
                                strokeLinejoin="round"
                                strokeWidth={2}
                                d="M4 16l4.586-4.586a2 2 0 012.828 0L16 16m-2-2l1.586-1.586a2 2 0 012.828 0L20 14m-6-6h.01M6 20h12a2 2 0 002-2V6a2 2 0 00-2-2H6a2 2 0 00-2 2v12a2 2 0 002 2z"
                            />
                        </svg>
                        <h4 className="text-md font-semibold text-[#1e293b]">
                            Hero Banner Content
                        </h4>
                    </div>

                    <div className="space-y-4">
                        {/* Hero Banner Title */}
                        <div className="space-y-2">
                            <Input
                                label="Hero Banner Title"
                                type="text"
                                value={heroBannerTitle || ''}
                                onChange={(e) => onHeroBannerTitleChange(e.target.value)}
                                placeholder="Experience the Finest Selection"
                                maxLength={60}
                                showCharCount
                            />
                            <p className="text-xs text-[#64748b]">
                                Main headline for your homepage banner (max 60 characters)
                            </p>
                        </div>

                        {/* Hero Banner Subtitle */}
                        <div className="space-y-2">
                            <div className="flex items-center justify-between">
                                <label className="block text-sm font-medium text-[#1e293b]">
                                    Hero Banner Subtitle
                                </label>
                                <span
                                    className={`text-xs ${(heroBannerSubtitle?.length || 0) > 160 ? 'text-[#ef4444]' : 'text-[#64748b]'}`}
                                >
                                    {heroBannerSubtitle?.length || 0}/160
                                </span>
                            </div>
                            <textarea
                                value={heroBannerSubtitle || ''}
                                onChange={(e) => onHeroBannerSubtitleChange(e.target.value)}
                                placeholder="Curated collections from around the world"
                                maxLength={160}
                                rows={2}
                                className="w-full px-4 py-2.5 bg-white border border-[#e2e8f0] rounded-lg text-[#1e293b] focus:outline-none focus:ring-2 focus:ring-[#6e64f0] focus:border-transparent resize-none"
                            />
                            <p className="text-xs text-[#64748b]">
                                Supporting text for hero banner (max 160 characters)
                            </p>
                        </div>

                        <div className="grid grid-cols-1 lg:grid-cols-3 gap-4">
                            {/* Highlight Label */}
                            <Input
                                label="Highlight Label (Optional)"
                                type="text"
                                value={heroBannerHighlightLabel || ''}
                                onChange={(e) => onHeroBannerHighlightLabelChange(e.target.value)}
                                placeholder="Limited Time Offer"
                                maxLength={40}
                            />

                            {/* CTA Text */}
                            <Input
                                label="CTA Button Text"
                                type="text"
                                value={heroBannerCtaText || ''}
                                onChange={(e) => onHeroBannerCtaTextChange(e.target.value)}
                                placeholder="Explore Collection"
                                maxLength={30}
                            />

                            {/* Redirection URL */}
                            <Input
                                label="CTA Redirection URL"
                                type="url"
                                value={heroBannerRedirectionUrl || ''}
                                onChange={(e) => onHeroBannerRedirectionUrlChange(e.target.value)}
                                placeholder="https://bottleshop.com/collection"
                            />
                        </div>
                    </div>
                </div>

                {/* SEO Section */}
                <div className="pt-4 border-t border-[#e2e8f0]">
                    <div className="flex items-center gap-2 mb-4 bg-[#f0fdf4] p-3 rounded-lg border border-[#bbf7d0]">
                        <svg
                            className="w-5 h-5 text-[#10b981]"
                            fill="none"
                            stroke="currentColor"
                            viewBox="0 0 24 24"
                        >
                            <path
                                strokeLinecap="round"
                                strokeLinejoin="round"
                                strokeWidth={2}
                                d="M21 21l-6-6m2-5a7 7 0 11-14 0 7 7 0 0114 0z"
                            />
                        </svg>
                        <h4 className="text-md font-semibold text-[#1e293b]">SEO Metadata</h4>
                    </div>

                    <div className="space-y-4">
                        {/* Meta Title */}
                        <div className="space-y-2">
                            <label className="block text-sm font-medium text-[#1e293b]">
                                Meta Title
                            </label>
                            <Input
                                type="text"
                                value={metaTitle || ''}
                                onChange={(e) => onMetaTitleChange(e.target.value)}
                                placeholder="Best Wine & Spirits Store - Premium Collection"
                                maxLength={60}
                            />
                            <p className="text-xs text-[#64748b]">
                                {metaTitle?.length || 0}/60 characters • Appears in search results
                            </p>
                        </div>

                        {/* Meta Description */}
                        <div className="space-y-2">
                            <div className="flex items-center justify-between">
                                <label className="block text-sm font-medium text-[#1e293b]">
                                    Meta Description
                                </label>
                                <span
                                    className={`text-xs ${(metaDescription?.length || 0) > 500 ? 'text-[#ef4444]' : 'text-[#64748b]'}`}
                                >
                                    {metaDescription?.length || 0}/500
                                </span>
                            </div>
                            <textarea
                                value={metaDescription || ''}
                                onChange={(e) => onMetaDescriptionChange(e.target.value)}
                                placeholder="Discover our premium collection of wines, spirits, and craft beers. Shop online or visit our store for expert recommendations."
                                maxLength={500}
                                rows={4}
                                className="w-full px-4 py-2.5 bg-white border border-[#e2e8f0] rounded-lg text-[#1e293b] focus:outline-none focus:ring-2 focus:ring-[#6e64f0] focus:border-transparent resize-none"
                            />
                            <p className="text-xs text-[#64748b]">
                                Brief description for search engines (max 500 characters)
                            </p>
                        </div>

                        {/* Keywords */}
                        <div className="space-y-2">
                            <label className="block text-sm font-medium text-[#1e293b]">
                                Keywords
                            </label>
                            <Input
                                type="text"
                                value={keywordInput}
                                onChange={(e) => setKeywordInput(e.target.value)}
                                onKeyDown={handleAddKeyword}
                                placeholder="Type a keyword and press Enter"
                            />
                            <p className="text-xs text-[#64748b]">
                                Add relevant keywords for SEO (press Enter to add)
                            </p>
                            {metaKeywords.length > 0 && (
                                <div className="flex flex-wrap gap-2 mt-3">
                                    {metaKeywords.map((keyword, index) => (
                                        <span
                                            key={index}
                                            className="inline-flex items-center gap-1.5 px-3 py-1.5 bg-[#f1f5f9] text-[#1e293b] rounded-full text-sm"
                                        >
                                            {keyword}
                                            <button
                                                type="button"
                                                onClick={() => handleRemoveKeyword(index)}
                                                className="hover:text-[#ef4444] transition-colors"
                                            >
                                                <svg
                                                    className="w-3.5 h-3.5"
                                                    fill="none"
                                                    stroke="currentColor"
                                                    viewBox="0 0 24 24"
                                                >
                                                    <path
                                                        strokeLinecap="round"
                                                        strokeLinejoin="round"
                                                        strokeWidth={2}
                                                        d="M6 18L18 6M6 6l12 12"
                                                    />
                                                </svg>
                                            </button>
                                        </span>
                                    ))}
                                </div>
                            )}
                        </div>

                        {/* OG Image */}
                        <ImageUploader
                            label="Social Media Preview Image (OG Image)"
                            value={ogImage}
                            onChange={onOgImageChange}
                            aspectRatio="1.91:1"
                            maxSize={3}
                        />
                    </div>
                </div>
            </CardContent>
        </Card>
    );
}
